import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useBlocker } from 'react-router';

const useUnsavedChanges = ({ isDirty, resetForm }) => {
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const navigate = useNavigate();

  // Block navigation when there are unsaved changes
  const blocker = useBlocker(isDirty);

  // Handle blocked navigation attempts
  useEffect(() => {
    if (blocker.state === 'blocked' && isDirty) {
      setShowUnsavedModal(true);
    }
  }, [blocker, isDirty]);

  // Handle browser refresh and tab close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        // Modern browsers require setting returnValue
        const message =
          'You have unsaved changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    // Add event listener for beforeunload
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clean up event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Modal handlers
  const handleStayOnPage = useCallback(() => {
    setShowUnsavedModal(false);
    blocker.reset(); // Reset the blocker
  }, [blocker]);

  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedModal(false);

    // Reset form if provided
    if (resetForm) {
      resetForm();
    }

    // Proceed with the blocked navigation
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker, resetForm]);

  // Custom navigation function
  const navigateTo = useCallback(
    (path) => {
      if (isDirty) {
        setShowUnsavedModal(true);
        navigate(path);
        return false;
      }
      navigate(path);
      return true;
    },
    [isDirty, navigate],
  );

  return {
    showUnsavedModal,
    handleStayOnPage,
    handleDiscardChanges,
    navigateTo,
  };
};

export default useUnsavedChanges;
